import { Helmet } from "react-helmet-async";
import { useEmbed } from "@/contexts/EmbedContext";
import { useLanguage, SUPPORTED_LANGUAGES } from "@/contexts/LanguageContext";

const BASE_URL = "https://guestlyexperiences.com";

const GLOBAL_ORG_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Guestly Homes Sweden AB",
  alternateName: "Guestly Experiences",
  url: BASE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${BASE_URL}/images/guestly-logo.png`,
    width: 512,
    height: 512,
  },
  sameAs: ["https://www.guestlyhomes.com"],
  description: "Curated local experiences for guests staying in premium short-term homes across Sweden.",
};

const GLOBAL_WEBSITE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Guestly Experiences",
  url: BASE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${BASE_URL}/experiences?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  ogImage?: string | null;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[] | null;
  breadcrumbs?: Array<{ name: string; href: string }>;
  noindex?: boolean;
}

export function SEOHead({ title, description, path, ogImage, jsonLd, breadcrumbs, noindex }: SEOHeadProps) {
  const { isEmbed } = useEmbed();
  const { language } = useLanguage();

  // Skip SEO tags inside iframe embed
  if (isEmbed) return null;

  const canonicalUrl = `${BASE_URL}${path}`;
  const fullTitle = `${title} | Guestly Experiences`;
  const image = ogImage || `${BASE_URL}/images/hero-experiences.webp`;

  const breadcrumbJsonLd = breadcrumbs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: `${BASE_URL}${b.href}`,
        })),
      }
    : null;

  return (
    <Helmet>
      <html lang={language} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Guestly Experiences" />
      <meta property="og:locale" content={language} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* hreflang for all supported languages + x-default */}
      {SUPPORTED_LANGUAGES.map((lang) => (
        <link
          key={lang.code}
          rel="alternate"
          hrefLang={lang.code}
          href={lang.code === "en" ? `${BASE_URL}${path}` : `${BASE_URL}/${lang.code}${path}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}${path}`} />

      {/* Global Organization JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(GLOBAL_ORG_JSON_LD)}</script>

      {/* Global WebSite JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(GLOBAL_WEBSITE_JSON_LD)}</script>

      {/* BreadcrumbList JSON-LD */}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      )}

      {/* Page-specific JSON-LD */}
      {jsonLd && (Array.isArray(jsonLd)
        ? jsonLd.map((ld, i) => (
            <script key={`ld-${i}`} type="application/ld+json">{JSON.stringify(ld)}</script>
          ))
        : <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
